import type {AutoCheckElement} from '@github/auto-check-element'

import {announce} from '@github-ui/aria-live'
import {fire} from 'delegated-events'
import {observe} from '@github-ui/selector-observer'
import {onInput} from '@github-ui/onfocus'
import {remoteForm} from '@github-ui/remote-form'
import {sendData} from '@github-ui/hydro-tracking'

function setCustomTierStatusIndicatorClass(target: Element, className: string) {
  target.classList.remove('status-indicator-success', 'status-indicator-loading', 'status-indicator-failed')
  target.classList.add(className)
}

remoteForm('.js-sponsors-custom-amount-form', async function (form, wants) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const statusIndicator = form.querySelector<HTMLElement>('.js-status-indicator')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const formGroup = form.querySelector<HTMLElement>('.form-group')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const errorNote = form.querySelector<HTMLElement>('.js-custom-amount-input-validation')!

  setCustomTierStatusIndicatorClass(statusIndicator, 'status-indicator-loading')

  try {
    await wants.json()
    errorNote.classList.remove('error')
    errorNote.hidden = true
    formGroup.classList.remove('errored')
    errorNote.textContent = ''
    setCustomTierStatusIndicatorClass(statusIndicator, 'status-indicator-success')
  } catch (error) {
    // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
    const message = error.response.json.message
    errorNote.classList.add('error')
    errorNote.hidden = false
    errorNote.textContent = message
    formGroup.classList.add('errored')
    setCustomTierStatusIndicatorClass(statusIndicator, 'status-indicator-failed')
  }
})

observe('.js-tier-shared-parent auto-check.js-tier-pricing-check', check => {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const parent = check.closest<HTMLElement>('.js-tier-shared-parent')!
  const publishButton = parent.querySelector<HTMLButtonElement>('.js-publish-tier-button')
  // PublishButton will not exist if the user is signed out
  if (publishButton) {
    check.addEventListener('error', () => (publishButton.disabled = true))
    check.addEventListener('load', () => (publishButton.disabled = false))
  }
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  fire(check.querySelector<HTMLInputElement>('input')!, 'input')
})

let customAmountDebounceTimer: ReturnType<typeof setTimeout> | undefined
let lastAnnouncedMessage = ''

document.addEventListener('turbo:before-cache', () => {
  clearTimeout(customAmountDebounceTimer)
  lastAnnouncedMessage = ''
})

onInput('.js-sponsors-custom-amount-input', function (event) {
  const input = event.target as HTMLInputElement
  const customAmount = parseInt(input.value, 10)
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = input.closest('.js-sponsors-custom-amount-container')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const message = container.querySelector('.js-sponsors-custom-amount-message')!

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const tierMinimums = JSON.parse(message.getAttribute('data-tier-minimums')!) as number[]
  const closestTierAmount = tierMinimums.find(amount => customAmount >= amount)
  const minCustomTier = message.getAttribute('data-min-custom-tier-amount')
  const minCustomTierAmount = minCustomTier == null || minCustomTier === '' ? 0 : parseInt(minCustomTier, 10)

  const tierRewardPrefix = message.getAttribute('data-tier-reward-prefix') || ''
  const tierRewardSuffix = message.getAttribute('data-tier-reward-suffix') || ''
  const defaultRewardText = message.getAttribute('data-default-reward-text') || ''
  const chooseAmountText = message.getAttribute('data-choose-amount-text') || ''

  // There are three cases:
  //
  // 1. The user chose an invalid amount (e.g. below min): choose a different amount
  // 2. The user chose a valid amount above or at a published tier: tier-relevant rewards
  // 3. The user chose a valid amount but below any published tiers (or no published tiers exist): default rewards
  //
  // N.B. We support publishing tiers that are below the minimum set for custom amounts. A custom amount that
  // collides with a published tier is considered valid.
  const newMessage = (() => {
    if (!customAmount || (customAmount < minCustomTierAmount && customAmount !== closestTierAmount)) {
      return chooseAmountText
    } else if (closestTierAmount) {
      return `${tierRewardPrefix} $${closestTierAmount} ${tierRewardSuffix}`
    } else {
      return defaultRewardText
    }
  })()

  // Update visible text immediately for sighted users.
  message.textContent = newMessage

  // Debounce the screen reader announcement so it fires once after the user
  // stops typing, and only when the message actually changed. Uses the
  // @github-ui/aria-live utility which manages its own live region element,
  // avoiding conflicts with the auto-check element's setCustomValidity calls
  // that cause VoiceOver to re-read the input.
  clearTimeout(customAmountDebounceTimer)
  customAmountDebounceTimer = setTimeout(() => {
    if (newMessage !== lastAnnouncedMessage) {
      lastAnnouncedMessage = newMessage
      announce(newMessage)
    }
  }, 1000)
})

function sponsorsNeedIdeasToggled(event: Event) {
  const details = event.currentTarget as HTMLElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const summaryEl = details.querySelector<HTMLElement>('.js-sponsors-toggle-need-help')!
  const isOpen = details.hasAttribute('open')
  let payload
  let hmac
  let hydroClientContext

  if (isOpen) {
    payload = summaryEl.getAttribute('data-open-hydro-click')
    hmac = summaryEl.getAttribute('data-open-hydro-click-hmac')
    hydroClientContext = summaryEl.getAttribute('data-open-hydro-client-context')
  } else {
    payload = summaryEl.getAttribute('data-closed-hydro-click')
    hmac = summaryEl.getAttribute('data-closed-hydro-click-hmac')
    hydroClientContext = summaryEl.getAttribute('data-closed-hydro-client-context')
  }

  payload = payload || ''
  hmac = hmac || ''
  hydroClientContext = hydroClientContext || ''

  sendData(payload, hmac, hydroClientContext)
}

observe('.js-sponsors-need-help-details', details => {
  details.addEventListener('toggle', sponsorsNeedIdeasToggled)
})

function updateTierFormFrequency(event: Event) {
  const radioButton = event.currentTarget as HTMLInputElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = radioButton.closest('.js-sponsors-tier-form')!

  const repositoriesMenu = container.querySelector('.js-repositories-menu') as HTMLElement
  repositoriesMenu.hidden = radioButton.value === 'one-time'

  const tierPricingCheck = container.querySelector('auto-check.js-tier-pricing-check') as AutoCheckElement
  const verifyUrl = new URL(tierPricingCheck.src, window.location.origin)
  verifyUrl.searchParams.set('frequency', radioButton.value)
  tierPricingCheck.src = verifyUrl.href
}

observe('.js-tier-frequency-radio-button', button => {
  button.addEventListener('change', updateTierFormFrequency)
})
